import {defaultFetch, httpMethod} from "@/scripts/Common/api";
import type {ApiResponse} from "@/scripts/Common/api";
import type {AccountingPartner, AppSetting, Customer, FetchAccountingPartnersParam, FetchCustomersParam, Service} from "@/components/customers/scripts/types";
import { getToken} from "@/scripts/Common";
import { ImportResult } from "@/scripts/Common";
import { useI18n } from "@/composables/useI18n";

const fetchCustomers = async (args: FetchCustomersParam = {}, abortSignal? : AbortSignal): Promise<ApiResponse<Customer[]>> => {

  const {
    response,
    error,
    paginationCount,
    paginationLimit,
    paginationPage,
    totalItemCount
  } = await defaultFetch({
    apiName: 'customers',
    bearer: getToken(),
    options: {
      query: {
        ...args,
        pageNumber: typeof args?.pageNumber === 'number' ? args.pageNumber - 1 : undefined, // reduce by 1 to make it 0 based
      },
      abortSignal
    }
  });

  return {
    response,
    error,
    paginationCount,
    paginationLimit,
    paginationPage,
    totalItemCount
  }
}

const fetchCustomer = async (id: number, abortSignal? : AbortSignal): Promise<ApiResponse<Customer>> => {

  // validate arguments
  if (id <= 0) return { error: 'Id must be greater than 0.' };

  const {response,
    error,
  } = await defaultFetch({
    apiName: 'customerResource',
    bearer: getToken(),
    options: {
      routeParams: {
        customerId: id.toString()
      },
      abortSignal
    }
  });

  return {
    response: response as Customer,
    error,
  }
}

const fetchCustomerAppSettings = async (accountingPartnerId: number): Promise<ApiResponse<AppSetting[]>> => await defaultFetch({
    apiName: 'customerAppSettings',
    options: {
        routeParams: {
            accountingPartnerId: accountingPartnerId.toString(10)
        }
    },
    bearer: getToken()
});

const fetchAccountingPartners = async (args: FetchAccountingPartnersParam, abortSignal? : AbortSignal): Promise<ApiResponse<AccountingPartner[]>> => {
  const {
    response,
    error,
    paginationCount,
    paginationLimit,
    paginationPage,
    totalItemCount
  } = await defaultFetch({
    apiName: 'accountingPartners',
    bearer: getToken(),
    options: {
      query: {
        ...args,
        pageNumber: typeof args?.pageNumber === 'number' ? args.pageNumber - 1 : undefined, // reduce by 1 to make it 0 based
      },
      abortSignal
    }
  });

  return {
    response: response as AccountingPartner[],
    error,
    paginationCount,
    paginationLimit,
    paginationPage,
    totalItemCount
  }
}

const fetchAccountingPartnerById = async (id: number): Promise<ApiResponse<AccountingPartner>> => {
  const {
    response,
    error
  } = await defaultFetch({
    apiName: 'accountingPartnerById',
    bearer: getToken(),
    options: {
      routeParams: {
        accountingPartnerId: id.toString()
      }
    }
  });

  return {
    response: response as AccountingPartner,
    error
  }
}

const fetchCustomerServices = async (): Promise<ApiResponse<Service[]>> => await defaultFetch({
    apiName: 'service',
    bearer: getToken()
});


const fetchDefaultProvision = async (): Promise<ApiResponse<number>> => await defaultFetch({
    apiName: 'defaultProvision',
    bearer: getToken()
});


const deleteCustomer = async (customerId: number): Promise<{ error: any }> => {
    const {
        error,
    } = await defaultFetch({
        apiName: 'customerResource',
        options: {
            method: httpMethod.DELETE,
            routeParams: {
                customerId: customerId.toString(10)
            }
        },
        bearer: getToken()
    });

    return {
        error,
    };
}

const createOrUpdateCustomer = async (customer: Customer, method: httpMethod.PUT | httpMethod.POST)
    : Promise<ApiResponse<number>> => await defaultFetch({
    apiName: 'customers',
    options: {
        method
    },
    body: JSON.stringify(customer),
    bearer: getToken()
});


const updateCustomerState = async (customer: Customer, action: 'contract' | 'confirm' | 'cancel')
    : Promise<ApiResponse<number>> => await defaultFetch({
    apiName: 'customerStateChange',
    options: {
        method: httpMethod.PUT,
        routeParams: {
            action
        }
    },
    body: JSON.stringify(customer),
    bearer: getToken()
});



const importCustomers = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file)

  const {locale} = useI18n();

  const {
    error,
    response,
  } = await defaultFetch({
    apiName: 'importCustomers',
    bearer: getToken(),
    options: {
      method: httpMethod.PUT,
      headers: {
        'Content-Type': 'text/csv',
        'Authorization': `Bearer ${getToken()}`,
        'Accept-Language': locale.value
      }
    },
    body: file,
  });

  return {
    error,
    response: response as ImportResult
  }
}



export {
    fetchCustomers,
    fetchCustomer,
    fetchCustomerAppSettings,
    deleteCustomer,
    createOrUpdateCustomer,
    fetchAccountingPartners,
    fetchAccountingPartnerById,
    fetchCustomerServices,
    fetchDefaultProvision,
    updateCustomerState,
    importCustomers
}
